import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, set } from "firebase/database";
import { FaEdit, FaEye, FaSave, FaTimes } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminProducts.css";
import { setPageMeta } from "../utils/seo";

// Import the actual brush images
import dinokids from "../assets/dino kids.webp";
import triaction from "../assets/triaction.webp";
import sweepy from "../assets/sweepy jr.webp";
import multicare from "../assets/whiteness multi care .webp";
import pureSoft from "../assets/sensiplus puresoft.webp";
import whiteness from "../assets/whiteness.webp";
import acrobat from "../assets/acrobat jr.webp";
import puresoft from "../assets/pure soft 2.webp";
import relax from "../assets/whiteness relaxation.webp";
import travelSet from "../assets/travelpack.webp";
import puresoft2 from "../assets/puresoft 1250.webp";

// Define the real brush products with actual images
const allProducts = [
  {
    id: 1,
    name: "TRIACTION TOOBRIGHT 511185",
    price: 160,
    image: whiteness,
    category: "adult",
    type: "whiteness",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring. Protective cap.",
  },
  {
    id: 2,
    name: "BANAT RELAXION 512152",
    price: 150,
    image: relax,
    category: "adult",
    type: "whiteness",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring. Protective cap.",
  },
  {
    id: 3,
    name: "BANAT WHITENESS MULTICARE",
    price: 180,
    image: multicare,
    category: "adult",
    type: "whiteness",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring. Protective cap.",
  },
  {
    id: 4,
    name: "BANAT TRIACTION TOOFRESH 511412",
    price: 160,
    image: triaction,
    category: "adult",
    type: "whiteness",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring. Protective cap.",
  },
  {
    id: 5,
    name: "PRO 6240 2 PACK 515665",
    price: 250,
    image: puresoft,
    category: "adult",
    type: "pure-soft",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring. Protective cap.",
  },
  {
    id: 6,
    name: "SENSIPLUS PURESOFT 516266",
    price: 150,
    image: pureSoft,
    category: "adult",
    type: "pure-soft",
    explanation: "The Tynex Feathered filaments provide the most sensitive and effective cleaning of teeth and gums.",
  },
  {
    id: 7,
    name: "PRO 12540 ULTRASOFT 516181",
    price: 200,
    image: puresoft2,
    category: "adult",
    type: "pure-soft",
    explanation: "The PBT bristles with specially rounded tips help prevent the tooth and gums from injuring.",
  },
  {
    id: 8,
    name: "UTRA WHITEBOXED 515825",
    price: 250,
    image: travelSet,
    category: "adult",
    type: "travel",
    explanation: "The nylon 6.12 bristles with specially rounded tips prevent the tooth and gums from injuring.",
  },
  {
    id: 9,
    name: "DINO KIDS 511117",
    price: 2,
    image: dinokids,
    category: "children",
    type: "standard",
    explanation: "With its toothbrush head suitable for the mouth structure of kids and straight bristle trim, it helps kids brush their teeth comfortably. Nylon 6.12 bristles with their ends rounded specifically help kids prevent damage on teeth and gingiva. With its handle suitable for hand ergonomy of kids, it helps easy gripping. Designed for 2-5 years old kids with baby teeth with protective cap.",
  },
  {
    id: 10,
    name: "SWEEPY JR",
    price: 120,
    image: sweepy,
    category: "children",
    type: "standard",
    explanation: "Ensure effective brushing with its top of the brush for children, thanks to its physiognomy conforming to that of mouth interior. Specially rounded tips of nylon 6.12 bristles do not bruise the teeth surface and gum. With protective cap.",
  },
  {
    id: 11,
    name: "ACROBAT JR",
    price: 200,
    image: acrobat,
    category: "children",
    type: "standard",
    explanation: "Ensures effective brushing with its top of the brush for children, to its physiognomy conforming to that of mouth interior. Specially rounded tips of nylon 6.12 bristles do not bruise the teeth surface and gum. Suitable for children who still have at school age secondary teeth together with their first teeth.",
  }
];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    setPageMeta({
      title: "Admin Products | Banat India",
      description:
        "Manage product listings and pricing in the Banat India admin panel.",
      keywords: "admin products, Banat India admin, product management",
    });
  }, []);
  

  const [editForm, setEditForm] = useState({
    name: "",
    price: "",
    category: "",
    type: "",
    explanation: ""
  });

  const categories = [
    "adult",
    "children", 
    "old-age",
    "bamboo",
    "charcoal",
    "miswak",
    "whiteness",
    "travel",
    "acrobat",
    "dino",
    "sweepy",
    "ufo",
    "pure-soft",
    "standard"
  ];

  const types = [
    "whiteness",
    "pure-soft", 
    "travel",
    "standard",
    "bamboo",
    "charcoal",
    "miswak"
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const database = getDatabase();
      
      // Load website products (original static products)
      const websiteProductsRef = ref(database, "websiteProducts");
      const websiteProductsSnapshot = await get(websiteProductsRef);
      
      let websiteProductsList = [];
      if (websiteProductsSnapshot.exists()) {
        const websiteProductsData = websiteProductsSnapshot.val();
        
        // Merge Firebase data with original allProducts to preserve image references
        websiteProductsList = allProducts.map(originalProduct => {
          const firebaseProduct = websiteProductsData[originalProduct.id];
          if (firebaseProduct) {
            return {
              ...originalProduct, // Keep original image
              name: firebaseProduct.name || originalProduct.name,
              price: firebaseProduct.price || originalProduct.price,
              category: firebaseProduct.category || originalProduct.category,
              type: firebaseProduct.type || originalProduct.type,
              explanation: firebaseProduct.explanation || originalProduct.explanation,
              discount: firebaseProduct.discount || 0,
              isEditable: true
            };
          } else {
            return {
              ...originalProduct,
              discount: 0,
              isEditable: true
            };
          }
        });
      } else {
        // Initialize with static products
        websiteProductsList = allProducts.map(product => ({
          ...product,
          discount: 0,
          isEditable: true
        }));
        
        // Save to Firebase
        const productsObject = {};
        websiteProductsList.forEach(product => {
          productsObject[product.id] = product;
        });
        
        await set(ref(database, "websiteProducts"), productsObject);
      }
      
      // Load uploaded products (from admin-product-images)
      const uploadedProductsRef = ref(database, "productImages");
      const uploadedProductsSnapshot = await get(uploadedProductsRef);
      
      let uploadedProductsList = [];
      if (uploadedProductsSnapshot.exists()) {
        const uploadedProductsData = uploadedProductsSnapshot.val();
        
        uploadedProductsList = Object.entries(uploadedProductsData).map(([id, image]) => {
          // Check if the image URL is a blob URL (which will be invalid)
          const isBlobUrl = image.imageUrl && image.imageUrl.startsWith('blob:');
          
          return {
            id: parseInt(id.replace('img-', '')) || Date.now(),
            name: image.productName,
            price: image.price || 150,
            discount: image.discount || 0,
            image: isBlobUrl ? "/banat logo .png" : image.imageUrl, // Use fallback for blob URLs
            category: image.category,
            type: image.category,
            explanation: image.description || "Uploaded product",
            isUploaded: true,
            isEditable: true,
            hasInvalidImage: isBlobUrl
          };
        });
      }
      
      // Combine both lists
      const allProductsCombined = [...websiteProductsList, ...uploadedProductsList];
      
      // Always set products state, even if empty
      setProducts(allProductsCombined);
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name,
      price: product.price.toString(),
      category: product.category,
      type: product.type,
      explanation: product.explanation,
      discount: product.discount ? product.discount.toString() : "0"
    });
  };

  const handleSave = async () => {
    if (!editForm.name || !editForm.price || !editForm.category) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const database = getDatabase();
      
      if (editingProduct.isUploaded) {
        // Update uploaded product (from admin-product-images)
        const productRef = ref(database, `productImages/img-${editingProduct.id}`);
        
        const updatedProduct = {
          productName: editForm.name,
          price: parseFloat(editForm.price),
          category: editForm.category,
          description: editForm.explanation,
          discount: parseFloat(editForm.discount) || 0,
          lastUpdated: new Date().toISOString()
        };
        
        await set(productRef, updatedProduct);
      } else {
        // Update original website product
        const productRef = ref(database, `websiteProducts/${editingProduct.id}`);
        
        const updatedProduct = {
          name: editForm.name,
          price: parseFloat(editForm.price),
          category: editForm.category,
          type: editForm.type,
          explanation: editForm.explanation,
          discount: parseFloat(editForm.discount) || 0,
          lastUpdated: new Date().toISOString()
        };
        
        await set(productRef, updatedProduct);
      }
      
      // Update local state
      setProducts(products.map(p => 
        p.id === editingProduct.id ? { ...p, ...editForm } : p
      ));
      
      setEditingProduct(null);
      alert("Product updated successfully!");
      
    } catch (error) {
      alert("Error updating product");
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
  };

  const getCategoryBadge = (category) => {
    const colors = {
      children: "#ff6b6b",
      adult: "#4ecdc4",
      "old-age": "#45b7d1",
      bamboo: "#96ceb4",
      charcoal: "#feca57",
      miswak: "#ff9ff3",
      whiteness: "#54a0ff",
      travel: "#5f27cd",
      acrobat: "#00d2d3",
      dino: "#ff9f43",
      sweepy: "#10ac84",
      ufo: "#ee5a24",
      "pure-soft": "#6c5ce7",
      standard: "#a29bfe"
    };
    
    return (
      <span 
        className="category-badge"
        style={{ backgroundColor: colors[category] || "#6c757d" }}
      >
        {category}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading products...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content-header">
        <h1>Website Products Management</h1>
        <p>Edit existing website products, prices, and descriptions</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Products</h3>
          <p className="stat-number">{products.length}</p>
        </div>
        <div className="stat-card">
          <h3>Categories</h3>
          <p className="stat-number">{new Set(products.map(p => p.category)).size}</p>
        </div>
        <div className="stat-card">
          <h3>With Discounts</h3>
          <p className="stat-number">{products.filter(p => p.discount > 0).length}</p>
        </div>
        <div className="stat-card">
          <h3>Average Price</h3>
          <p className="stat-number">₹{Math.round(products.reduce((sum, p) => sum + p.price, 0) / products.length)}</p>
        </div>
      </div>

            <div className="products-grid">
        {products.length === 0 ? (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem',
            color: '#666'
          }}>
            <p>No products found. Loading from database...</p>
            <p>Products count: {products.length}</p>
            <p>Loading state: {loading ? 'Loading...' : 'Loaded'}</p>
            <button 
              onClick={() => {
                const testProducts = allProducts.map(product => ({
                  ...product,
                  discount: 0,
                  isEditable: true
                }));
                setProducts(testProducts);
              }}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Load Test Products
            </button>
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="product-card" style={{ border: '2px solid #ddd', padding: '10px' }}>
              <div className="product-image-container">
                <img src={product.image} alt={product.name} className="product-image" />
              </div>
                          <div className="product-info">
              <h4>{product.name}</h4>
              {product.isUploaded && (
                <span style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  display: 'inline-block'
                }}>
                  UPLOADED
                </span>
              )}
              <div className="product-meta">
                {getCategoryBadge(product.category)}
                <span className="product-type">{product.type}</span>
              </div>
                <div className="price-section">
                  {product.discount && product.discount > 0 ? (
                    <div className="price-with-discount">
                      <span className="original-price">₹{product.price}</span>
                      <span className="discounted-price">₹{Math.round(product.price * (1 - product.discount / 100))}</span>
                      <span className="discount-badge">-{product.discount}%</span>
                    </div>
                  ) : (
                    <p className="product-price">₹{product.price}</p>
                  )}
                </div>
                <p className="product-description">{product.explanation}</p>
              </div>
              <div className="product-actions" style={{ 
                display: 'flex', 
                gap: '10px', 
                padding: '10px', 
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                marginTop: '10px'
              }}>
                <button 
                  onClick={() => {
                    handleEdit(product);
                  }}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    minWidth: '100px',
                    justifyContent: 'center'
                  }}
                >
                  <FaEdit style={{ fontSize: '16px' }} /> Edit
                </button>
                <button 
                  onClick={() => window.open(`/product/${product.id}`, '_blank')}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    minWidth: '100px',
                    justifyContent: 'center'
                  }}
                >
                  <FaEye style={{ fontSize: '16px' }} /> View
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingProduct && (
        <div className="modal-overlay" onClick={handleCancel} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Product: {editingProduct.name}</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  placeholder="Enter product name"
                />
              </div>
              <div className="form-group">
                <label>Price (₹) *</label>
                <input
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                  placeholder="Enter product price"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  value={editForm.discount}
                  onChange={(e) => setEditForm({...editForm, discount: e.target.value})}
                  placeholder="Enter discount percentage"
                  min="0"
                  max="100"
                  step="1"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                >
                  <option value="">Select type</option>
                  {types.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editForm.explanation}
                  onChange={(e) => setEditForm({...editForm, explanation: e.target.value})}
                  placeholder="Enter product description"
                  rows="4"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCancel}
              >
                <FaTimes /> Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSave}
              >
                <FaSave /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProducts; 